import React from 'react';
import { AlertTriangle } from 'lucide-react';
import Button from './Button';
import useModalBehavior from '@/hooks/useModalBehavior';
import type { ConfirmOptions } from '@/contexts/ConfirmContext';

interface ConfirmDialogProps extends ConfirmOptions {
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Rendered by ConfirmProvider — reach for `useConfirm()` rather than mounting
 * this directly.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  tone = 'danger',
  onConfirm,
  onCancel,
}) => {
  const ref = useModalBehavior(onCancel);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onCancel}
      role="presentation"
    >
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby={message ? 'confirm-message' : undefined}
        // The backdrop closes the dialog; clicks inside it must not bubble up
        // and do the same.
        onClick={(e) => e.stopPropagation()}
        className="app-panel app-border border rounded-2xl shadow-depth w-full max-w-sm p-6 animate-scale-in"
      >
        <div className="flex items-start gap-3.5">
          <div
            className={`flex-shrink-0 p-2.5 rounded-xl border ${
              tone === 'danger'
                ? 'bg-red-500/10 border-red-500/20'
                : 'bg-accent/10 border-accent/20'
            }`}
          >
            <AlertTriangle
              className={`h-5 w-5 ${tone === 'danger' ? 'text-red-400' : 'text-accent'}`}
            />
          </div>
          <div className="min-w-0">
            <h2 id="confirm-title" className="text-base font-bold text-ink">
              {title}
            </h2>
            {message && (
              <p id="confirm-message" className="text-sm text-ink-dim mt-1.5 leading-relaxed">
                {message}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2.5 mt-6">
          {/* Cancel sits first and takes the initial focus, so an accidental
              Enter on an unread dialog backs out instead of confirming. */}
          <Button variant="outline" onClick={onCancel} className="flex-1">
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            className="flex-1"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
